import React, { cloneElement, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { TextAreaInput } from '../common/text-area-input';
import { ClearIcon, HIcon } from './assets';

interface PersonalIntroModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  introduction: string;
  onUseTemplate?: () => void;
  changeValue?: (name: string, value: string) => void;
  onOpenChange?: (open: boolean) => void;
}

/** 个人简介弹窗 */
export const PersonalIntroModal: React.FC<PersonalIntroModalProps> = ({
  trigger,
  open: externalOpen,
  introduction,
  changeValue,
  onUseTemplate,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const toast = useToast();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[578px] max-h-[830px] min-h-[830px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4">
          <Dialog.Title
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <span>{t('agent_dashboard.user_detail.agent_info.intro_edit')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col p-6 pt-0 select-none'>
          <div className='flex-1 w-full border border-[rgba(208,213,221,1)] rounded-[16px] overflow-hidden pt-5'>
            <TextAreaInput
              type="text"
              value={introduction}
              placeholder={t('agent_dashboard.user_detail.agent_info.intro_placeholder')}
              autoFocus
              className='h-[39.625rem] outline-none border-none pt-0 !font-normal text-sm placeholder:text-sm'
              onChange={(e) => changeValue('introduction', e.target.value)}
              maxLength={20000}
            />
            <div className='flex items-center justify-between px-5 mt-4'>
              <div
                className='w-[94px] h-8 bg-[rgba(242,244,247,1)] rounded-[8px] text-[13px] font-semibold text-[rgba(16,24,40,1)] flex items-center justify-center hover:bg-gray-200 cursor-pointer transition-all'
                onClick={onUseTemplate}
              >
                <div className='flex items-center gap-1'>
                  <HIcon />
                  {t('agent_dashboard.user_detail.agent_info.example_title')}
                </div>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-sm font-medium text-[#98A2B3]'>{introduction?.length || 0}/20000</span>
                <div onClick={() => changeValue('introduction', '')}>
                  <ClearIcon className={cn(
                    'size-5 text-[rgba(208,213,221,1)]',
                    introduction?.length && 'text-[rgba(102,112,133,1)]  cursor-pointer'
                  )} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}